import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { api } from 'controllers/network/apiClient';
import { RESPONSES_QUERY_KEYS } from '../constants';
import { Subscriber, SubscriberResponse } from '../types';

interface Options {
  refetchInterval?: number;
  onSuccess?: (data: Subscriber) => void;
}

export const useSubscriber = (
  subscriberId?: number,
  { refetchInterval, onSuccess }: Options = {},
): UseQueryResult<Subscriber> => {
  return useQuery({
    queryKey: [RESPONSES_QUERY_KEYS.SUBSCRIBER, subscriberId],
    queryFn: () => {
      return api.get<SubscriberResponse>(`/subscribers/${subscriberId}`);
    },
    select: ({ data }) => ({
      ...data,
      customer: data.customer === 'Subscriber' ? null : data.customer,
    }),
    enabled: !!subscriberId,
    ...(refetchInterval ? { refetchInterval } : {}),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
  });
};

type UseSubscriberMutationResult<TVariables = unknown> = UseMutationResult<
  void,
  unknown,
  TVariables,
  unknown
>;

interface UseSendMessageOptions {
  autoResolve?: boolean;
}

export const useSendMessage = (
  subscriberId?: number,
  options?: UseSendMessageOptions,
): UseSubscriberMutationResult<any> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      if (!subscriberId) throw new Error('No subscriberId provided');
      const params = new URLSearchParams();
      if (options?.autoResolve !== undefined) {
        params.append('auto_resolve', options.autoResolve ? 'true' : 'false');
      }
      return api.post(`/message?${params.toString()}`, payload);
    },
    onMutate: (payload: any) => {
      const previousMessages = client.getQueryData([
        RESPONSES_QUERY_KEYS.SUBSCRIBER,
        subscriberId,
      ]);

      // optimistic update
      client.setQueryData(
        [RESPONSES_QUERY_KEYS.SUBSCRIBER, subscriberId],
        (old: any) => ({
          ...old,
          chat_history: [
            ...old.data.chat_history,
            {
              body: payload.message.text,
            },
          ],
        }),
      );
      return { previousMessages };
    },
    onError: (err, newTodo, context: any) => {
      client.setQueryData(
        [RESPONSES_QUERY_KEYS.SUBSCRIBER, subscriberId],
        context.previousMessages,
      );
    },
    // Always refetch after error or success:
    onSettled: () => {
      client.invalidateQueries({
        queryKey: [RESPONSES_QUERY_KEYS.SUBSCRIBER, subscriberId],
      });
      client.invalidateQueries({ queryKey: [RESPONSES_QUERY_KEYS.RESPONSES] });
      client.invalidateQueries({ queryKey: [RESPONSES_QUERY_KEYS.SEGMENTS] });
    },
  });
};

export const useOptOutSubscriber = (
  subscriberId?: number,
): UseSubscriberMutationResult<void> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!subscriberId) throw new Error('No subscriberId provided');
      return api.post('/optout', {
        subscriber_id: subscriberId,
      });
    },
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: [RESPONSES_QUERY_KEYS.SUBSCRIBER, subscriberId],
      });
      client.invalidateQueries({
        queryKey: [RESPONSES_QUERY_KEYS.UNRESOLVED_RESPONSES],
      });
    },
  });
};

export const useResolveSubscriber = (
  subscriberId?: number,
): UseSubscriberMutationResult<'resolve' | 'unresolve'> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (resolveType) => {
      if (!subscriberId) throw new Error('No subscriberId provided');
      return api.post(`/responses/${resolveType}`, {
        subscriber_id: subscriberId,
      });
    },
    onMutate: () => {
      const previousSubscriber = client.getQueryData([
        RESPONSES_QUERY_KEYS.SUBSCRIBER,
        subscriberId,
      ]);

      // optimistic update
      client.setQueryData(
        [RESPONSES_QUERY_KEYS.SUBSCRIBER, subscriberId],
        (old: any) => ({
          ...old,
          data: {
            ...old.data,
            subscriber: {
              ...old.data.subscriber,
              is_unresolved: !old.data.subscriber.is_unresolved,
            },
          },
        }),
      );
      return { previousSubscriber };
    },
    onError: (err, resolveType, context: any) => {
      client.setQueryData(
        [RESPONSES_QUERY_KEYS.SUBSCRIBER, subscriberId],
        context.previousSubscriber,
      );
    },
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: [RESPONSES_QUERY_KEYS.SUBSCRIBER, subscriberId],
      });
      client.invalidateQueries({
        queryKey: [RESPONSES_QUERY_KEYS.UNRESOLVED_RESPONSES],
      });
    },
  });
};

export default useSubscriber;
